import { Suspense, useState, useEffect } from 'react';
import type { GetStaticProps } from 'next';
import { getAppI18nConfig, initI18n } from 'cfgi18n';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';

import { FullRegisterForm } from '@login/patterns';
import {
    getFullRegister,
    GetFullRegisterResponse,
    getSimpleFooter,
    GetSimpleFooterResponse,
} from 'services/idm';
import { apiErrorHandler } from '@login/helpers';

export type staticProps = {
    footerData: GetSimpleFooterResponse;
};

export const getStaticProps = (async (params) => {
    const locale = params?.locale as string;
    const { supportedLocales } = await getAppI18nConfig('login');
    await initI18n(locale, supportedLocales);
    const footerData = (await getSimpleFooter(locale)) || [];
    return { props: { footerData, locale } };
}) satisfies GetStaticProps<staticProps>;

const FullRegister = () => {
    const { t, i18n } = useTranslation();
    const { push } = useRouter();
    const [fullRegisterData, setFullRegisterData] =
        useState<GetFullRegisterResponse | null>(null);

    useEffect(() => {
        fetchFullRegister();
    }, []);

    const fetchFullRegister = async () => {
        const data = await getFullRegister(i18n.language).catch((e) => apiErrorHandler(e, i18n.language));
        if (data) {
            setFullRegisterData(data);

            if (data.action === 'Redirect' && data.redirectUrl) {
                if (data.redirectUrl.startsWith('/connect')) {
                    const absoluteUrl = `${window.location.origin}${data.redirectUrl}`;
                    window.location.href = absoluteUrl;
                } else {
                    push(data.redirectUrl);
                }
            }
        }
    };

    return (
        <Suspense>
            {fullRegisterData && fullRegisterData.action === 'Display' && (
                <FullRegisterForm
                    urls={{
                        registerUrl: fullRegisterData.placeholders?.registerUrl,
                        signInUrl: fullRegisterData.placeholders?.signInUrl,
                        forgottenPasswordUrl:
                            fullRegisterData.placeholders?.forgottenPasswordUrl,
                        privacyPolicyUrl:
                            fullRegisterData.placeholders?.privacyPolicyUrl,
                        supportName: fullRegisterData.placeholders?.supportName,
                        siteName: fullRegisterData.placeholders?.siteName,
                        minimumAge: fullRegisterData.placeholders?.minimumAge,
                    }}
                    marketingMessage={fullRegisterData.marketingMessage}
                    disclaimerMessage={fullRegisterData.disclaimerMessage}
                    token={fullRegisterData?.xsrfToken}
                    countries={fullRegisterData.countries}
                    channelSpecificFields={fullRegisterData.channelSpecificFields}
                />
            )}
        </Suspense>
    );
};

export default FullRegister;
